import React from "react";

/**
 * Pillar article content — "Cómo activarte cuando llevas años sin moverte".
 *
 * Central thesis: "El problema no es saber qué hacer. Es volver a empezar."
 * Both language versions share the same structure and emotional voice.
 *
 * Editorial guidelines:
 *  - Speak in "tú" (informal singular). Never "usted".
 *  - No guru-speak, no false promises, no jargon.
 *  - Acknowledge prior failures explicitly so the reader feels seen.
 *  - End each section with a sentence the reader could highlight.
 */

// ───────────────────── shared primitives (same look & feel as the rest of the site) ─────────────────────

function Section({ title, children, id }) {
  return (
    <section className="space-y-4" id={id}>
      <h2 className="font-sans-display text-2xl sm:text-3xl text-[#0B2545] tracking-tight leading-snug mt-12 mb-2">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Pullquote({ children }) {
  return (
    <blockquote className="border-l-2 border-[#B4C5A4] pl-5 py-1 my-6 font-sans-display text-lg sm:text-xl text-[#0B2545] tracking-tight leading-snug italic">
      {children}
    </blockquote>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//                                CATALÀ
// ════════════════════════════════════════════════════════════════════════════
export function PillarActivacioCA() {
  return (
    <>
      <p>
        {"Si has arribat fins aquí, probablement no és la primera vegada. Has llegit altres articles, has provat altres mètodes, fa setmanes o anys que dius que aquesta vegada sí. I aquí ets, una altra vegada, llegint sobre activar-te. Vull començar dient-te una cosa abans de res: el problema no ets tu."}
      </p>
      <p>
        {"Aquest article no és una llista de consells. És un mirall. Si t'hi veus, ens entendrem. Si no, segurament aquest no és el moment, i tampoc passa res."}
      </p>

      <Section title="El problema no és saber què fer. És tornar a començar." id="problema">
        <p>
          {"Si fessin un examen sobre què és saludable, l'aprovaries. Saps que has de moure't més, que la fruita és millor que el sucre, que dormir vuit hores ajuda. Ho saps. Però saber-ho no t'ha mogut. I això no et fa una persona vaga o sense voluntat — et fa una persona normal."}
        </p>
        <p>
          {"El que separa els que canvien dels que no, no és la informació. És tornar a començar quan no t'apetix. És fer un dia el que vas decidir el dia anterior, fins i tot quan dormies malament, et fan mal els genolls, has dinat tard, t'ha trucat la teva mare amb un problema, i la idea de moure't sembla absurda."}
        </p>
        <Pullquote>
          {"Saber què fer és el 10%. Tornar a començar quan ja has fallat és el 90%."}
        </Pullquote>
      </Section>

      <Section title="La trampa del dilluns" id="dilluns">
        <p>
          {"La història la coneixes: el divendres a la nit decideixes que dilluns comences. T'imagines amb energia, comprant fruita, sortint a caminar, dormint bé. El cap de setmana fas excessos perquè dilluns ja comences. Dilluns t'aixeques i el cos no està d'humor. O ho intentes dos dies i el dimecres t'has saltat el plan. Dijous penses que potser dilluns que ve."}
        </p>
        <p>
          {"Aquest cicle no és culpa teva. És el resultat d'un mètode equivocat. El mètode del dilluns demana que canviïs molt — i de cop. Demana que la persona d'avui actuï com una persona que fa anys que ja s'ha activat. I tu encara no estàs activat. Per això falla."}
        </p>
      </Section>

      <Section title="El que ningú t'ha explicat sobre la vergonya" id="vergonya">
        <p>
          {"La vergonya és la barrera que menys es parla, però la que més et frena. T'imagines anant a un gimnàs i et veus en un mirall, envoltat de cossos que es mouen ràpid mentre tu et quedes sense aire al cap d'un minut. T'imagines a una classe i tothom et mira. T'imagines comprant unes sabatilles esportives i la dependenta pensa el que pensa."}
        </p>
        <p>
          {"No t'estàs inventant la vergonya. Forma part d'haver tingut un cos jutjat durant anys — pels metges, pels familiars que es preocupen, pels comentaris al supermercat, per les fotos d'instagram. La vergonya no és debilitat: és memòria emocional del teu cos."}
        </p>
        <Pullquote>
          {"Que et faci vergonya no vol dir que siguis feble. Vol dir que has acumulat mirades."}
        </Pullquote>
        <p>
          {"El problema és que la resposta habitual — \"oblida la vergonya i fes-ho igualment\" — no funciona. Forçar la vergonya l'amplifica. El que funciona és començar en un lloc on la vergonya no apareix: a casa, sol, amb un mètode que no exigeix cap actuació pública."}
        </p>
      </Section>

      <Section title="El dolor que sents no és culpa teva" id="dolor">
        <p>
          {"Et fan mal els genolls quan camines més de quinze minuts. Et fa mal l'esquena quan estàs molt temps assegut. Et costa lligar-te les sabates. T'aixeques de la cadira amb una mà recolzada. Aquestes coses no són la teva culpa: són la conseqüència previsible d'anys amb un cos que ha hagut de carregar més pes del que estava preparat, i amb una musculatura que s'ha anat adormint per falta d'ús."}
        </p>
        <p>
          {"Aquí hi ha una contradicció cruel: et fa mal moure't, així que no et mous. I com menys et mous, més et fa mal quan ho intentes. La sortida no és aguantar el dolor (això empitjora les coses) ni esperar que desaparegui sol (no ho farà). És començar amb moviments tan petits que el dolor no apareix, i pujar el nivell molt lentament."}
        </p>
        <p>
          {"Hi ha estudis sobrats que demostren que persones amb dolor crònic de genolls poden recuperar mobilitat fent exercicis de força específics durant 8-12 setmanes — sense córrer, sense gimnàs, sense saltar. Però comencen amb 5 minuts. No amb 30."}
        </p>
      </Section>

      <Section title="Per què tornar a fer dieta no és la resposta" id="dieta">
        <p>
          {"Si has fet dieta abans, ja saps com acaba. Els primers dies fas miracles, el primer mes baixes una mica, el segon mes et bloqueges, el tercer abandones, i sis mesos després has recuperat el pes — i sovint una mica més. No és perquè siguis feble. És perquè el teu cos no està dissenyat per restringir calories de cop: està dissenyat per sobreviure quan ho fas."}
        </p>
        <p>
          {"A més, hi ha una cosa que poca gent diu: pots perdre pes i seguir tenint el mateix problema. Pots tenir 15 quilos menys i continuar sense poder pujar escales sense ofegar-te. El pes és un número. La capacitat funcional és la teva vida real."}
        </p>
        <Pullquote>
          {"Pots pesar menys i no poder fer res més. O pesar el mateix i recuperar la teva vida."}
        </Pullquote>
      </Section>

      <Section title="El cos no s'ha trencat. S'ha adaptat." id="cos">
        <p>
          {"Aquest és el missatge més important d'aquest article: el teu cos no està espatllat. El teu cos s'ha adaptat al que has demanat d'ell. Si fa anys que el demanes seure 10 hores al dia i moure't poc, s'ha tornat eficient en això. Si comences a demanar-li altres coses — a poc a poc, sense pressa — s'adapta a aquelles altres coses."}
        </p>
        <p>
          {"Hi ha estudis fascinants de persones de 70 anys sedentàries que en 12 setmanes d'entrenament suau guanyen un 30% de força muscular. Persones de 80 anys que recuperen equilibri amb exercicis senzills. El cos no té data de caducitat per adaptar-se. Però sí que necessita que li demanis."}
        </p>
        <p>
          {"L'única cosa que no es recupera fàcil és el temps perdut esperant el dilluns perfecte."}
        </p>
      </Section>

      <Section title="Recuperar capacitat ABANS que perdre pes" id="capacitat">
        <p>
          {"A AI METABOLIC pensem el revés del que estàs acostumat a sentir. No comencem per la dieta. No comencem per perdre pes. Comencem per recuperar capacitat funcional — la capacitat per fer el que necessites cada dia sense dolor, sense ofegar-te, sense por de caure."}
        </p>
        <p>{"Per què?"}</p>
        <p>
          {"Primer, perquè és el que de veritat et canvia la vida. Perdre dos quilos no canvia res significatiu. Poder agafar al teu nét en braços o pujar les escales del metro sense parar, sí. Aquesta és la victòria que t'ho fa seguir."}
        </p>
        <p>
          {"Segon, perquè quan recuperes capacitat tornes a moure't sense forçar-te — i el moviment et porta els altres canvis (dormir millor, gana més estable, energia, hormones) que sí que faciliten la pèrdua de pes. Però el pes és el resultat, no el punt de partida."}
        </p>
        <p>
          {"Tercer, perquè la confiança no torna parlant amb tu mateix davant del mirall. Torna fent coses que el teu cos respongui — i tu te n'adonis."}
        </p>
      </Section>

      <Section title="El primer pas real (el de veritat, no el d'Instagram)" id="primer-pas">
        <p>
          {"Si t'has llegit altres articles, ja saps com sona el discurs habitual: \"comença caminant 10.000 passos al dia\", \"fes 30 minuts de fitness 5 dies per setmana\". Aquest discurs està fet per algú que no és tu. Per al teu cos avui, el primer pas real probablement és:"}
        </p>
        <ul className="list-disc pl-5 space-y-2 my-4">
          <li>{"Aixecar-te de la cadira 5 vegades seguides, una vegada al dia."}</li>
          <li>{"Caminar 5 minuts després de dinar — només 5, no 20."}</li>
          <li>{"Pujar 1 tram d'escales en lloc d'agafar l'ascensor, una vegada al dia."}</li>
          <li>{"Mantenir-te dret 10 minuts mentre parles per telèfon, en comptes d'asseure't."}</li>
        </ul>
        <p>
          {"Sembla ridícul. I aquí és exactament on et vol l'algorisme social: pensant que això és poc i que cal fer més. No. Començar petit és el que ningú més et dirà perquè no ven. Però és el que funciona quan fa anys que no et mous."}
        </p>
      </Section>

      <Section title="Què canvia quan no ho fas sol" id="acompanyament">
        <p>
          {"L'altre punt clau és que no ho facis sol. No perquè sigui impossible, sinó perquè ho hauràs intentat sol abans i ja saps com acaba. La motivació individual té una vida útil curta — sovint dues setmanes. Després t'oblides, et despistes, t'estanques."}
        </p>
        <p>
          {"AI METABOLIC està dissenyat per ser el contrari: una presència diària, suau, no intrusiva, que t'ajuda a fer el petit pas d'avui — sense exigir-te energia per recordar-te de fer-lo. És el missatge de \"vas bé\" quan no et sents bé. És l'ajust del plan quan vas tres dies sense fer res. És no haver de tornar-ho a començar tot des de zero cada cop."}
        </p>
        <Pullquote>
          {"L'adherència no és voluntat. És tenir algú al costat que faci les coses possibles d'imaginar."}
        </Pullquote>
      </Section>

      <Section title="Una pregunta abans de marxar" id="pregunta">
        <p>
          {"Si t'has llegit fins aquí, probablement t'has identificat amb alguna part. La nostra feina és construir alguna cosa que de veritat t'ajudi — no una altra app de fitness, no una altra dieta, no una altra promesa buida. Per fer-ho bé necessitem entendre què és el que més et pesa avui."}
        </p>
        <p>
          {"Has trobat el formulari habitual al final dels articles. Aquesta vegada t'hem deixat una pregunta diferent. Si vols dedicar 10 segons a contestar-la, ens ajudaràs a saber on hem de posar primer l'atenció. I si vols, deixa el teu correu i t'avisem quan tinguem alguna cosa que t'encaixi."}
        </p>
      </Section>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════════
//                                CASTELLANO
// ════════════════════════════════════════════════════════════════════════════
export function PillarActivacionES() {
  return (
    <>
      <p>
        {"Si has llegado hasta aquí, probablemente no es la primera vez. Has leído otros artículos, has probado otros métodos, llevas semanas o años diciendo que esta vez sí. Y aquí estás, otra vez, leyendo sobre activarte. Quiero empezar diciéndote una cosa antes que nada: el problema no eres tú."}
      </p>
      <p>
        {"Este artículo no es una lista de consejos. Es un espejo. Si te ves en él, nos entenderemos. Si no, probablemente este no es el momento, y tampoco pasa nada."}
      </p>

      <Section title="El problema no es saber qué hacer. Es volver a empezar." id="problema">
        <p>
          {"Si hicieran un examen sobre qué es saludable, lo aprobarías. Sabes que tienes que moverte más, que la fruta es mejor que el azúcar, que dormir ocho horas ayuda. Lo sabes. Pero saberlo no te ha movido. Y eso no te hace una persona vaga o sin voluntad — te hace una persona normal."}
        </p>
        <p>
          {"Lo que separa a los que cambian de los que no, no es la información. Es volver a empezar cuando no te apetece. Es hacer un día lo que decidiste el día anterior, incluso cuando dormiste mal, te duelen las rodillas, has comido tarde, te ha llamado tu madre con un problema, y la idea de moverte parece absurda."}
        </p>
        <Pullquote>
          {"Saber qué hacer es el 10%. Volver a empezar cuando ya has fallado es el 90%."}
        </Pullquote>
      </Section>

      <Section title="La trampa del lunes" id="lunes">
        <p>
          {"La historia la conoces: el viernes por la noche decides que el lunes empiezas. Te imaginas con energía, comprando fruta, saliendo a caminar, durmiendo bien. El fin de semana haces excesos porque el lunes ya empiezas. El lunes te levantas y el cuerpo no está de humor. O lo intentas dos días y el miércoles ya te has saltado el plan. El jueves piensas que quizá el lunes que viene."}
        </p>
        <p>
          {"Este ciclo no es culpa tuya. Es el resultado de un método equivocado. El método del lunes te pide que cambies mucho — y de golpe. Te pide que la persona de hoy actúe como una persona que lleva años ya activada. Y tú aún no estás activado. Por eso falla."}
        </p>
      </Section>

      <Section title="Lo que nadie te ha explicado sobre la vergüenza" id="verguenza">
        <p>
          {"La vergüenza es la barrera de la que menos se habla, pero la que más te frena. Te imaginas yendo a un gimnasio y te ves en un espejo, rodeado de cuerpos que se mueven rápido mientras tú te quedas sin aire al cabo de un minuto. Te imaginas en una clase y todos te miran. Te imaginas comprando unas zapatillas deportivas y la dependienta piensa lo que piensa."}
        </p>
        <p>
          {"No te estás inventando la vergüenza. Forma parte de haber tenido un cuerpo juzgado durante años — por los médicos, por los familiares que se preocupan, por los comentarios en el supermercado, por las fotos de Instagram. La vergüenza no es debilidad: es memoria emocional de tu cuerpo."}
        </p>
        <Pullquote>
          {"Que te dé vergüenza no significa que seas débil. Significa que has acumulado miradas."}
        </Pullquote>
        <p>
          {"El problema es que la respuesta habitual — \"olvida la vergüenza y hazlo igualmente\" — no funciona. Forzar la vergüenza la amplifica. Lo que funciona es empezar en un lugar donde la vergüenza no aparece: en casa, solo, con un método que no exige ninguna actuación pública."}
        </p>
      </Section>

      <Section title="El dolor que sientes no es culpa tuya" id="dolor">
        <p>
          {"Te duelen las rodillas cuando caminas más de quince minutos. Te duele la espalda cuando estás mucho rato sentado. Te cuesta atarte los zapatos. Te levantas de la silla con una mano apoyada. Estas cosas no son culpa tuya: son la consecuencia previsible de años con un cuerpo que ha tenido que cargar más peso del que estaba preparado, y con una musculatura que se ha ido apagando por falta de uso."}
        </p>
        <p>
          {"Aquí hay una contradicción cruel: te duele moverte, así que no te mueves. Y cuanto menos te mueves, más te duele cuando lo intentas. La salida no es aguantar el dolor (eso empeora las cosas) ni esperar que desaparezca solo (no lo hará). Es empezar con movimientos tan pequeños que el dolor no aparece, e ir subiendo el nivel muy lentamente."}
        </p>
        <p>
          {"Hay estudios de sobra que demuestran que personas con dolor crónico de rodillas pueden recuperar movilidad haciendo ejercicios de fuerza específicos durante 8-12 semanas — sin correr, sin gimnasio, sin saltar. Pero empiezan con 5 minutos. No con 30."}
        </p>
      </Section>

      <Section title="Por qué volver a hacer dieta no es la respuesta" id="dieta">
        <p>
          {"Si has hecho dieta antes, ya sabes cómo acaba. Los primeros días haces milagros, el primer mes bajas un poco, el segundo mes te bloqueas, el tercero abandonas, y seis meses después has recuperado el peso — y a menudo un poco más. No es porque seas débil. Es porque tu cuerpo no está diseñado para restringir calorías de golpe: está diseñado para sobrevivir cuando lo haces."}
        </p>
        <p>
          {"Además, hay algo que poca gente dice: puedes perder peso y seguir teniendo el mismo problema. Puedes tener 15 kilos menos y seguir sin poder subir escaleras sin ahogarte. El peso es un número. La capacidad funcional es tu vida real."}
        </p>
        <Pullquote>
          {"Puedes pesar menos y no poder hacer nada más. O pesar lo mismo y recuperar tu vida."}
        </Pullquote>
      </Section>

      <Section title="El cuerpo no se ha roto. Se ha adaptado." id="cuerpo">
        <p>
          {"Este es el mensaje más importante de este artículo: tu cuerpo no está estropeado. Tu cuerpo se ha adaptado a lo que has pedido de él. Si llevas años pidiéndole estar sentado 10 horas al día y moverte poco, se ha vuelto eficiente en eso. Si empiezas a pedirle otras cosas — poco a poco, sin prisa — se adapta a esas otras cosas."}
        </p>
        <p>
          {"Hay estudios fascinantes de personas de 70 años sedentarias que en 12 semanas de entrenamiento suave ganan un 30% de fuerza muscular. Personas de 80 años que recuperan equilibrio con ejercicios sencillos. El cuerpo no tiene fecha de caducidad para adaptarse. Pero sí necesita que se lo pidas."}
        </p>
        <p>
          {"Lo único que no se recupera fácil es el tiempo perdido esperando el lunes perfecto."}
        </p>
      </Section>

      <Section title="Recuperar capacidad ANTES que perder peso" id="capacidad">
        <p>
          {"En AI METABOLIC pensamos al revés de lo que estás acostumbrado a oír. No empezamos por la dieta. No empezamos por perder peso. Empezamos por recuperar capacidad funcional — la capacidad para hacer lo que necesitas cada día sin dolor, sin ahogarte, sin miedo a caer."}
        </p>
        <p>{"¿Por qué?"}</p>
        <p>
          {"Primero, porque es lo que de verdad te cambia la vida. Perder dos kilos no cambia nada significativo. Poder coger a tu nieto en brazos o subir las escaleras del metro sin parar, sí. Esa es la victoria que te hace seguir."}
        </p>
        <p>
          {"Segundo, porque cuando recuperas capacidad vuelves a moverte sin forzarte — y el movimiento te trae los otros cambios (dormir mejor, hambre más estable, energía, hormonas) que sí facilitan la pérdida de peso. Pero el peso es el resultado, no el punto de partida."}
        </p>
        <p>
          {"Tercero, porque la confianza no vuelve hablando contigo mismo delante del espejo. Vuelve haciendo cosas que tu cuerpo responde — y tú te das cuenta."}
        </p>
      </Section>

      <Section title="El primer paso real (el de verdad, no el de Instagram)" id="primer-paso">
        <p>
          {"Si has leído otros artículos, ya sabes cómo suena el discurso habitual: \"empieza caminando 10.000 pasos al día\", \"haz 30 minutos de fitness 5 días por semana\". Ese discurso está hecho para alguien que no eres tú. Para tu cuerpo de hoy, el primer paso real probablemente es:"}
        </p>
        <ul className="list-disc pl-5 space-y-2 my-4">
          <li>{"Levantarte de la silla 5 veces seguidas, una vez al día."}</li>
          <li>{"Caminar 5 minutos después de comer — solo 5, no 20."}</li>
          <li>{"Subir 1 tramo de escaleras en lugar de coger el ascensor, una vez al día."}</li>
          <li>{"Mantenerte de pie 10 minutos mientras hablas por teléfono, en vez de sentarte."}</li>
        </ul>
        <p>
          {"Parece ridículo. Y aquí es exactamente donde te quiere el algoritmo social: pensando que esto es poco y que hay que hacer más. No. Empezar pequeño es lo que nadie más te dirá porque no vende. Pero es lo que funciona cuando llevas años sin moverte."}
        </p>
      </Section>

      <Section title="Qué cambia cuando no lo haces solo" id="acompanamiento">
        <p>
          {"El otro punto clave es que no lo hagas solo. No porque sea imposible, sino porque ya lo habrás intentado solo antes y ya sabes cómo acaba. La motivación individual tiene una vida útil corta — a menudo dos semanas. Después te olvidas, te despistas, te estancas."}
        </p>
        <p>
          {"AI METABOLIC está diseñado para ser lo contrario: una presencia diaria, suave, no intrusiva, que te ayuda a hacer el pequeño paso de hoy — sin exigirte energía para acordarte de hacerlo. Es el mensaje de \"vas bien\" cuando no te sientes bien. Es el ajuste del plan cuando llevas tres días sin hacer nada. Es no tener que empezarlo todo de cero cada vez."}
        </p>
        <Pullquote>
          {"La adherencia no es voluntad. Es tener a alguien al lado que haga las cosas posibles de imaginar."}
        </Pullquote>
      </Section>

      <Section title="Una pregunta antes de irte" id="pregunta">
        <p>
          {"Si has leído hasta aquí, probablemente te has identificado con alguna parte. Nuestro trabajo es construir algo que de verdad te ayude — no otra app de fitness, no otra dieta, no otra promesa vacía. Para hacerlo bien necesitamos entender qué es lo que más te pesa hoy."}
        </p>
        <p>
          {"Has encontrado el formulario habitual al final de los artículos. Esta vez te hemos dejado una pregunta diferente. Si quieres dedicar 10 segundos a contestarla, nos ayudarás a saber dónde tenemos que poner primero la atención. Y si quieres, deja tu correo y te avisamos cuando tengamos algo que te encaje."}
        </p>
      </Section>
    </>
  );
}
